// Web port of contexts/UnreadChatsContext.js (the Chats badge, in mobile's units).
//
// useUnreadChats() -> null outside the provider, else {
//   badgeCount             chats with unread + pending incoming requests (the Chats nav badge),
//                          leaving out the open, focused room (lib/chatsBadge.js)
//   count                  per-notification count (back-compat with mobile)
//   unreadChatIds          distinct chat keys with an unread chat-message notice
//                          (E-Board = `<chatId>#eboard`, DM = conversation id)
//   lastChatEvents         [{ kind, chatId, notificationId, at }] for every chat that got a new
//                          notice in the latest snapshot (new array per qualifying snapshot)
//   incomingConnections    pending incoming connection requests (for an optional Friends badge)
//   connectionsError       true while the connections listener is failing (incomingConnections is stale)
//   badgeError             true while the connections or DM-threads listener is failing (badgeCount
//                          is stale, not current); both re-open with backoff and on tab focus
//   recomputeCount()       recompute from the local copy (no Firestore round trip)
//   getUnreadChatNotices() current unread notification docs (for the orphan sweep)
//   getDmConversationIds() DM ids, or null while the DM list is not loaded / possibly truncated
//   setOpenRoom(room)      { chatId, chatType } | null; use useReportOpenChat below instead
// }
// useReportOpenChat(chatId, chatType, active) publishes the room on screen while `active`.
//
// Differences from mobile, by design:
// - Notifications come from the ONE shared listener (hooks/useNotificationsSnapshot.js, owned by
//   UnreadProvider) through feed.onChanges; this file never opens a second listener on that query
//   and never imports context/UnreadContext.jsx (that file imports this one).
// - AppState "active" -> document visibilitychange; the OS icon badge (expo-notifications) is dropped.
// - Club / sub-chat read markers are device-local (utils/chatReadTracker -> localStorage, same
//   `chat_last_read_` prefix, scoped per account by the web AsyncStorage shim as
//   `chat_last_read_<uid>:<chatId>`, lib/storageKeys.js), so "read on web, unread on phone" is
//   expected, but one account's reads never hide another's. DMs sync through the server last_read_at.
// The provider sits inside the uid-keyed AccountScope, so an account switch remounts it; every
// effect is keyed on uid as well and genRef drops a stale recompute.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import { useNotificationsFeed } from "../hooks/useNotificationsSnapshot";
import { signedInAs } from "../lib/session";
import { getChatLastReadAt, getEboardLastReadAt, subscribeChatRead } from "../../../utils/chatReadTracker";
import { toMillis } from "../../../utils/chatMessageCore";
import { eboardBaseId, legacyEboardNoticeIds, standaloneDmRequestIds, unreadKeyFor } from "../../../utils/chatUnread";
import { chatsBadgeCount } from "../lib/chatsBadge";
import { NotificationService } from "../../../services/notificationService";
import { ConnectionService } from "../../../services/connectionService";
import { ConversationService } from "../../../services/conversationService";

// subscribeMyConversations is limit(100): at/over that the id list may be incomplete, so it must
// not be used to decide a DM notice is orphaned.
const DM_LIST_LIMIT = 100;
const RETRY_MAX = 6; // listener re-opens after an error: 5 s, 10 s, ... capped at 60 s

// Firestore ends a listener on error. Re-open it with capped backoff and, once the retries ran out,
// again each time the tab comes back into view (same policy as hooks/useNotificationsSnapshot.js).
// open(onOk, onFail) -> unsubscribe; a snapshot handler applies data only when onOk() returns true; setDown(bool) reports whether the listener is currently failing.
function keepListening(open, setDown) {
  let stopped = false;
  let unsub = null;
  let timer = null;
  let retries = 0;
  const start = () => {
    if (stopped) return;
    let failed = false;
    const fail = () => {
      if (failed || stopped) return;
      failed = true;
      setDown(true);
      if (typeof unsub === "function") unsub();
      unsub = null;
      if (retries >= RETRY_MAX) return;
      timer = setTimeout(() => { timer = null; start(); }, Math.min(60000, 5000 * 2 ** retries));
      retries += 1;
    };
    const ok = () => {
      if (failed || stopped) return false; // a late snapshot from a closed listener: ignore it
      retries = 0;
      setDown(false);
      return true;
    };
    const u = open(ok, fail);
    // An error raised synchronously inside open() ran before `u` existed: close it now.
    if (failed) { if (typeof u === "function") u(); } else unsub = u;
  };
  const onVisible = () => {
    if (stopped || document.visibilityState !== "visible" || unsub || timer) return;
    retries = 0;
    start();
  };
  start();
  document.addEventListener("visibilitychange", onVisible);
  return () => {
    stopped = true;
    document.removeEventListener("visibilitychange", onVisible);
    if (timer) clearTimeout(timer);
    if (typeof unsub === "function") unsub();
    unsub = null;
  };
}

const UnreadChatsContext = createContext(null);

export const useUnreadChats = () => useContext(UnreadChatsContext);

/** Reports the room on screen while `active` (mounted, ready, tab visible); cleared on unmount. */
export function useReportOpenChat(chatId, chatType, active) {
  const setOpenRoom = useContext(UnreadChatsContext)?.setOpenRoom;
  useEffect(() => {
    if (!setOpenRoom || !active || !chatId) return undefined;
    const room = { chatId, chatType };
    setOpenRoom(room);
    // Only clear our own entry: the next room may already have published itself.
    return () => setOpenRoom((cur) => (cur === room ? null : cur));
  }, [setOpenRoom, chatId, chatType, active]);
}

const sameList = (a, b) => a.join("|") === b.join("|");

export function UnreadChatsProvider({ children }) {
  const { user, emailVerified } = useAuth();
  const uid = user && emailVerified ? user.uid : null;
  const feed = useNotificationsFeed();
  const onChanges = feed?.onChanges;

  const [count, setCount] = useState(0);
  const [lastChatEvents, setLastChatEvents] = useState(null);
  const [unreadChatIds, setUnreadChatIds] = useState([]);
  const [incomingFrom, setIncomingFrom] = useState([]); // requester uids of pending connections
  const [dmRequests, setDmRequests] = useState([]); // [{ id, otherUid, connection_note }]
  const [declinedDmIds, setDeclinedDmIds] = useState([]); // requests I declined (no row)
  const [openRoom, setOpenRoom] = useState(null); // { chatId, chatType } of the focused room
  const [connDown, setConnDown] = useState(false); // connections listener failing (counts are stale)
  const [convosDown, setConvosDown] = useState(false); // DM threads listener failing

  const genRef = useRef(0); // bumped per recompute and per uid
  const uidRef = useRef(uid);
  uidRef.current = uid;
  const noticesRef = useRef(new Map()); // notification id -> doc (unread only)
  const dmIdsRef = useRef(null); // null = DM list not loaded (or possibly truncated)
  const dmReadAtRef = useRef(new Map()); // conversation id -> { readAt, fullyRead }
  const dmListFullRef = useRef(false);
  const dmLoadedRef = useRef(false);

  const incomingConnections = incomingFrom.length;
  const standaloneDmRequests = useMemo(() => standaloneDmRequestIds(dmRequests, incomingFrom), [dmRequests, incomingFrom]);
  // A pending DM request also has a dm_request notice (an unread chat id): count it once, as a request.
  const badgeCount = useMemo(
    () => chatsBadgeCount({
      unreadChatIds,
      dmRequestIds: dmRequests.map((r) => r.id),
      declinedDmIds,
      incomingConnections,
      standaloneDmRequests: standaloneDmRequests.length,
      openRoom,
    }),
    [unreadChatIds, dmRequests, declinedDmIds, incomingConnections, standaloneDmRequests, openRoom]
  );

  const getUnreadChatNotices = useCallback(() => [...noticesRef.current.values()], []);
  const getDmConversationIds = useCallback(() => dmIdsRef.current, []);

  // Same rule as mobile: a chat-message notice counts while its chat has no local read marker
  // newer than the notice (E-Board: its own key with the general fallback; DM: the server
  // last_read_at as well). Latest call wins.
  const recomputeCount = useCallback(async () => {
    const gen = ++genRef.current;
    const notices = [...noticesRef.current.values()];
    if (notices.length === 0) {
      setCount(0);
      setUnreadChatIds((prev) => (prev.length ? [] : prev));
      return;
    }
    const flags = await Promise.all(
      notices.map(async (n) => {
        const key = unreadKeyFor(n);
        if (!key) return false; // not a chat-message notification
        const createdMs = n.created_at ? toMillis(n.created_at) : 0;
        if (!createdMs) return false;
        let lastReadAt = key.kind === "eboard" ? await getEboardLastReadAt(eboardBaseId(key.chatId)) : await getChatLastReadAt(key.chatId);
        if (key.kind === "dm") {
          if (!dmLoadedRef.current) return false; // no DM rows until the list loads
          const server = dmReadAtRef.current.get(key.chatId);
          if (server?.fullyRead) return false;
          if (!server && dmListFullRef.current) return false; // beyond the newest DM_LIST_LIMIT threads
          lastReadAt = Math.max(lastReadAt, server?.readAt || 0);
        }
        return createdMs > lastReadAt ? key.chatId : false;
      })
    );
    if (gen !== genRef.current) return;
    const ids = [...new Set(flags.filter(Boolean))].sort();
    setCount(flags.filter(Boolean).length);
    setUnreadChatIds((prev) => (sameList(prev, ids) ? prev : ids));
  }, []);

  // Notifications: consume the shared feed (all docChanges, not only "added"). A subscriber that
  // joins late gets one replay of every doc as "added", like mobile's own first snapshot.
  useEffect(() => {
    genRef.current += 1;
    noticesRef.current = new Map();
    setCount(0);
    setUnreadChatIds([]);
    setLastChatEvents(null);
    if (!uid || typeof onChanges !== "function") return undefined;
    let alive = true;
    const unsubscribe = onChanges(({ changes, initial }) => {
      if (!alive || uidRef.current !== uid) return;
      // A full replay (late join, or the shared listener re-opened after an error): rebuild from
      // scratch, since removals during an outage are not reported. Only notices this provider had
      // not seen yet count as new chat events.
      const before = noticesRef.current;
      if (initial) noticesRef.current = new Map();
      // Every distinct chat that got a new notice in THIS batch (deduped within the batch).
      const chatAdds = new Map();
      (changes || []).forEach(({ type, doc }) => {
        if (!doc?.id) return;
        if (type === "removed") {
          noticesRef.current.delete(doc.id);
          return;
        }
        noticesRef.current.set(doc.id, doc);
        if (type === "added" && !(initial && before.has(doc.id))) {
          const key = unreadKeyFor(doc);
          if (key) chatAdds.set(`${key.kind}:${key.chatId}`, { ...key, notificationId: doc.id, at: Date.now() });
        }
      });
      if (chatAdds.size > 0) setLastChatEvents([...chatAdds.values()]);
      recomputeCount();
    });
    return () => {
      alive = false;
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [uid, onChanges, recomputeCount]);

  // Local read markers changed (a room read, a DM answered): recompute after the write resolved.
  // The E-Board room also reports its message ids, so notices from before the E-Board tag are
  // marked read. Only for a read made by this account, re-checked right before each write.
  useEffect(
    () =>
      subscribeChatRead((key, meta) => {
        const base = eboardBaseId(key);
        const mine = !!uid && meta?.uid === uid;
        const ids = base && mine && meta.messageIds ? legacyEboardNoticeIds([...noticesRef.current.values()], base, meta.messageIds) : [];
        ids.forEach((id) => {
          if (!signedInAs(uid)) return;
          NotificationService.markNotificationAsRead(id, uid).catch(() => {});
        });
        recomputeCount();
      }),
    [recomputeCount, uid]
  );

  // Pending incoming requests (connection + DM message requests) and DM read state, live.
  useEffect(() => {
    setIncomingFrom([]);
    setDmRequests([]);
    setDeclinedDmIds([]);
    dmIdsRef.current = null;
    dmReadAtRef.current = new Map();
    dmListFullRef.current = false;
    dmLoadedRef.current = false;
    setConnDown(false);
    setConvosDown(false);
    if (!uid) return undefined;
    const stopConn = keepListening(
      (onOk, onFail) =>
        ConnectionService.subscribeMyConnections(
          (list) => {
            if (!onOk()) return;
            const from = (Array.isArray(list) ? list : []).filter((c) => c.state === "incoming").map((c) => c.otherUid).sort();
            setIncomingFrom((prev) => (sameList(prev, from) ? prev : from));
          },
          // A listener error is not "no requests": keep the last known ones (shown as unknown).
          onFail
        ),
      setConnDown
    );
    const stopConvos = keepListening(
      (onOk, onFail) =>
        ConversationService.subscribeMyConversations(
          uid,
          (list) => {
            if (!onOk()) return;
            const convos = Array.isArray(list) ? list : [];
            // A request I declined has no row: leave it out so the sweep clears its leftover notice.
            const declinedByMe = (c) => c.status === "declined" && c.requester_uid !== uid;
            dmListFullRef.current = convos.length >= DM_LIST_LIMIT;
            dmLoadedRef.current = true;
            dmIdsRef.current = convos.length >= DM_LIST_LIMIT ? null : convos.filter((c) => !declinedByMe(c)).map((c) => c.id);
            const declined = convos.filter(declinedByMe).map((c) => c.id);
            setDeclinedDmIds((prev) => (sameList(prev, declined) ? prev : declined));
            const reqs = convos
              .filter((c) => c.status === "request" && c.requester_uid !== uid)
              .map((c) => ({ id: c.id, otherUid: c.otherUid, connection_note: c.connection_note === true }));
            const sig = (rows) => rows.map((r) => `${r.id}:${r.connection_note}`).join("|");
            setDmRequests((prev) => (sig(prev) === sig(reqs) ? prev : reqs));
            dmReadAtRef.current = new Map(
              convos.map((c) => {
                const readAt = toMillis(c.last_read_at?.[uid]) || 0;
                const last = c.last_message;
                // Same test as the DM row: a fan-out notice can land after the message was read.
                const fullyRead = !last || last.sender_id === uid || (!!readAt && toMillis(last.at) <= readAt);
                return [c.id, { readAt, fullyRead }];
              })
            );
            recomputeCount();
          },
          // A listener error is not "no threads": the sweep must skip DM notices.
          (error) => {
            dmIdsRef.current = null;
            onFail(error);
          }
        ),
      setConvosDown
    );
    return () => {
      stopConn();
      stopConvos();
    };
  }, [uid, recomputeCount]);

  // Tab shown again (mobile: app foreground): recompute, since a chat may have been read elsewhere.
  useEffect(() => {
    if (!uid) return undefined;
    const onVisible = () => {
      if (document.visibilityState === "visible") recomputeCount();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [uid, recomputeCount]);

  const value = useMemo(
    () => ({
      count,
      badgeCount,
      unreadChatIds,
      lastChatEvents,
      incomingConnections,
      connectionsError: connDown,
      badgeError: connDown || convosDown,
      recomputeCount,
      getUnreadChatNotices,
      getDmConversationIds,
      setOpenRoom,
    }),
    [count, badgeCount, unreadChatIds, lastChatEvents, incomingConnections, connDown, convosDown, recomputeCount, getUnreadChatNotices, getDmConversationIds]
  );

  return <UnreadChatsContext.Provider value={value}>{children}</UnreadChatsContext.Provider>;
}
